<template>
  <!--
    Ne pas remettre la classe Bulma « modal » ici : ce composant est deja rendu a
    l'interieur d'un b-modal, et cette classe applique un position:fixed plein ecran
    qui recouvrait le bouton de fermeture de Buefy — visible, mais inatteignable.
  -->
  <div class="cga-modal">
    <div class="modal-inner">
      <div class="modal-content">
        <div v-if="loading" class="cga-status">
          <b-icon icon="spinner" custom-class="fa-spin" />
          <span>{{$t('cgu.loading')}}</span>
        </div>

        <div v-else-if="loadError" class="cga-status">
          {{$t('cgu.error')}}
        </div>

        <div v-show="!loading && !loadError" class="pdf-container">
          <canvas ref="pdfCanvas"></canvas>
        </div>
      </div>

      <div class="controls" v-show="!loading && !loadError">
        <button @click="prevPage" :disabled="pageNum <= 1">{{$t('cgu.previous')}}</button>
        <span> {{$t('cgu.page', { current: pageNum, total: numPages })}} </span>
        <button @click="nextPage" :disabled="pageNum >= numPages">{{$t('cgu.next')}}</button>
      </div>

      <div class="controls" >
        <b-button @click="accepteCGA" :disabled="loading || loadError">
          {{$t('cgu.accepter')}}
        </b-button>
        <b-button @click="refuserCGA">
          {{$t('cgu.refuser')}}
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import pdfjsLib from 'pdfjs-dist'

export default {
  data () {
    return {
      pdf: null,
      pageNum: 1,
      numPages: 0,
      pdfUrl: '', // URL of the PDF to load
      // La fenetre s'ouvre avant que le document soit disponible : ces deux etats portent
      // le temoin d'attente et le message d'echec.
      loading: true,
      loadError: false,
    }
  },
  props: {
    // Chaine (URL) ou binaire : pdf.js accepte les deux, et le contenu arrive en
    // ArrayBuffer.
    documentUrl: [String, ArrayBuffer],

    // Transmis explicitement par l'appelant, et non deduit de l'URL : le « + » ouvre la
    // fenetre sans naviguer, l'URL ne porte donc pas le portefeuille.
    portefeuilleId: [String, Number],
  },
  computed: {
    ...mapState(['user']),
  },
  watch: {
    // Le contenu est demande apres l'ouverture de la fenetre : il faut donc reagir a
    // son arrivee, et non seulement le lire au montage.
    documentUrl (value) {
      if (value) {
        this.open(value)
      }
    },
  },
  mounted () {
    if (this.documentUrl) {
      this.open(this.documentUrl)
    }
  },
  methods: {
    // L'enregistrement est attendu avant la fermeture : l'API met l'etat CGA en
    // cache, et un nouveau clic sur le portefeuille avant la fin de l'appel lirait
    // une entree perimee.
    async saveCga (val) {
      const id = Number(this.portefeuilleId)

      // M-Files attend un entier : une valeur non entiere fait echouer la requete avec
      // « Could not convert string to integer ».
      if (!Number.isInteger(id) || id <= 0) {
        console.error('Identifiant de portefeuille invalide :', this.portefeuilleId)
        this.apiError('Portefeuille non identifié, enregistrement impossible.')
        return
      }

      try {
        await this.$store.dispatch('setCgaValue', {
          user: this.user.name,
          id: id,
          val: val
        })
      } catch (e) {
        this.apiError(e)
        return
      }

      this.$emit('close-modal')
    },
    accepteCGA () {
      return this.saveCga(true)
    },
    refuserCGA () {
      return this.saveCga(false)
    },
    open (pdfUrl) {
      this.pdfUrl = pdfUrl
      this.isVisible = true
      this.loadPdf(pdfUrl)
    },
    close () {
      this.isVisible = false
    },
    loadPdf (source) {
      // Le contenu arrive desormais en binaire, plus en objet JSON : pdf.js accepte
      // une URL ou un { data }. Les deux formes restent acceptees.
      const src = typeof source === 'string' ? source : { data: source }

      this.loading = true
      this.loadError = false

      pdfjsLib.getDocument(src).promise.then(
        (pdfDoc_) => {
          this.pdf = pdfDoc_
          this.numPages = pdfDoc_.numPages
          this.loading = false
          // Le rendu suit l'affichage du canvas, masque tant que loading vaut vrai.
          this.$nextTick(() => this.renderPage(this.pageNum))
        },
        (error) => {
          console.error('Error loading PDF: ', error)
          this.loading = false
          this.loadError = true
        }
      )
    },
    renderPage (num) {
      this.pdf.getPage(num).then((page) => {
        const canvas = this.$refs.pdfCanvas
        const context = canvas.getContext('2d')
        const viewport = page.getViewport({ scale: 1.5 }) // Adjust scale for zoom

        // Set the canvas dimensions to match the PDF page
        canvas.height = viewport.height
        canvas.width = viewport.width

        // Render the page to the canvas context
        page.render({ canvasContext: context, viewport: viewport })
      })
    },
    nextPage () {
      if (this.pageNum < this.numPages) {
        this.pageNum++
        this.renderPage(this.pageNum)
      }
    },
    prevPage () {
      if (this.pageNum > 1) {
        this.pageNum--
        this.renderPage(this.pageNum)
      }
    },
  }
}
</script>

<style scoped>
.modal-content {
  background-color: #fff;
  padding: 20px;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  width: 100%;
  height: 100%;
  margin: 0 auto;
}
.cga-modal .modal-content {
    width: 100%;
    max-height: calc(100vh - 100px);
}
/* Pas de position:fixed : le b-modal englobant assure deja le centrage et le
   plein ecran. En ajouter un ici recouvrait le bouton de fermeture. */
.cga-modal {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
}
.modal-inner {
  background-color: white;
  border-radius: 0.5em;
  margin: auto;
}
.controls{
  padding: 2px;
  max-width: fit-content;
  margin-left: auto;
  margin-right: auto;
}

/* Les boutons de pagination ne portent aucune classe Bulma : sans cette regle, ils
   heritent du "cursor: default" que le navigateur applique aux <button>. Ecartes
   lorsqu'ils sont desactives, en premiere et derniere page.
   Ce bloc de style est en CSS pur : pas d'imbrication ni de commentaire //. */
.controls button:not(:disabled) {
  cursor: pointer;
}
.cga-status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6em;
  min-width: 22em;
  min-height: 8em;
  padding: 2em;
  text-align: center;
  color: #7f7f7f;
}
</style>
